import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Languages, X, ChevronLeft, ChevronRight, MapPin, MessageCircle, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

interface PublicFreelancer {
  id: string;
  display_name: string;
  location: string;
  work_line_id: string;
  work_whatsapp: string;
  age: number | null;
  height: string;
  photo1_url: string;
  photo2_url: string;
}

const ProfilesPage = () => {
  const { t, toggleLanguage, language } = useLanguage();
  const [profiles, setProfiles] = useState<PublicFreelancer[]>([]);
  const [lightbox, setLightbox] = useState<{ photos: string[]; alt: string; index: number } | null>(null);

  useEffect(() => {
    fetch(`${API}/api/freelancers`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setProfiles)
      .catch(() => {});
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const step = useCallback((dir: number) => {
    setLightbox((l) => (l ? { ...l, index: (l.index + dir + l.photos.length) % l.photos.length } : l));
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, closeLightbox, step]);

  const openLightbox = (f: PublicFreelancer, startIndex: number) =>
    setLightbox({ photos: [`${API}${f.photo1_url}`, `${API}${f.photo2_url}`], alt: f.display_name, index: startIndex });

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white" data-testid="profiles-page">
      <header className="pt-6 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="profiles-back-btn">
            <Button variant="ghost" size="sm" className="gap-2 text-purple-700 hover:text-purple-900 hover:bg-purple-100">
              <ArrowLeft className="w-4 h-4" />
              {t("home.backToHome")}
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={toggleLanguage} className="gap-2 border-purple-300 text-purple-700" data-testid="profiles-lang-toggle">
            <Languages className="w-4 h-4" />
            {language === "en" ? "ไทย" : "EN"}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 pb-20 pt-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-purple-700 tracking-tight" data-testid="profiles-title">
            {t("freelance.profilesTitle")}
          </h1>
          <p className="mt-3 text-base text-gray-600 max-w-2xl mx-auto">{t("freelance.profilesSubtitle")}</p>
          <Link to="/freelance" data-testid="profiles-register-cta">
            <Button className="mt-5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-full px-8 gap-2">
              <UserPlus className="w-4 h-4" />
              {t("freelance.registerCta")}
            </Button>
          </Link>
        </div>

        {profiles.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground" data-testid="profiles-empty">{t("freelance.galleryEmpty")}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="profiles-grid">
            {profiles.map((f) => (
              <div key={f.id} className="flex flex-col" data-testid={`profile-card-${f.id}`}>
                <button
                  type="button"
                  onClick={() => openLightbox(f, 0)}
                  data-testid={`profile-photo-${f.id}`}
                  className="group relative w-full aspect-square overflow-hidden rounded-xl border border-purple-100 shadow-sm bg-purple-50"
                >
                  <img
                    src={`${API}${f.photo1_url}`}
                    alt={f.display_name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute bottom-1.5 right-1.5 bg-black/55 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                    1/2
                  </span>
                </button>
                <h3 className="mt-2 font-semibold text-sm text-gray-800 truncate" data-testid={`profile-name-${f.id}`} title={f.display_name}>
                  {f.display_name}
                </h3>
                {(f.age || f.height) && (
                  <p className="text-xs text-gray-500 truncate">
                    {[f.age ? `${f.age} ${t("freelance.yearsOld")}` : null, f.height || null].filter(Boolean).join(" · ")}
                  </p>
                )}
                {f.location && (
                  <p className="flex items-center gap-1 text-xs text-gray-600 mt-0.5 truncate" title={f.location}>
                    <MapPin className="w-3 h-3 text-purple-500 shrink-0" />
                    <span className="truncate">{f.location}</span>
                  </p>
                )}
                <div className="mt-1 space-y-0.5 text-xs">
                  {f.work_line_id && (
                    <p className="flex items-center gap-1 text-gray-700 truncate" title={f.work_line_id}>
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-[#00B900] text-white text-[8px] font-bold shrink-0">L</span>
                      <span className="truncate">{f.work_line_id}</span>
                    </p>
                  )}
                  {f.work_whatsapp && (
                    <p className="flex items-center gap-1 text-gray-700 truncate" title={f.work_whatsapp}>
                      <MessageCircle className="w-3 h-3 text-[#25D366] shrink-0" />
                      <span className="truncate">{f.work_whatsapp}</span>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Full-size lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          data-testid="profile-lightbox"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            data-testid="lightbox-close"
            aria-label={t("freelance.close")}
            className="absolute top-4 right-4 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {lightbox.photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); step(-1); }}
                data-testid="lightbox-prev"
                aria-label="Previous"
                className="absolute left-3 sm:left-6 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); step(1); }}
                data-testid="lightbox-next"
                aria-label="Next"
                className="absolute right-3 sm:right-6 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}

          <img
            src={lightbox.photos[lightbox.index]}
            alt={lightbox.alt}
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
            className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
          />

          {lightbox.photos.length > 1 && (
            <div className="absolute bottom-5 flex gap-2">
              {lightbox.photos.map((_, i) => (
                <span key={i} className={`w-2.5 h-2.5 rounded-full ${i === lightbox.index ? "bg-white" : "bg-white/40"}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfilesPage;
